import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Mail, Paperclip, RefreshCw, Search } from "lucide-react";
import { Context } from "../../context/Context";
import { Button, EmailStatusTag, EmptyState, Input, Pagination, SectionHeader, Select, StatCard, Table, Tags } from "../ui";
import { listMailsAPI, getMailFacetsAPI } from "../../utils/api/mails";
import MailDetails from "./MailDetails";
import { MAIL_TEXT, SOURCE_LABELS, TYPE_LABELS, formatDateTime, labelFor } from "./mailText";
import "./MailInbox.css";

const PAGE_SIZES = [10, 25, 50, 100];
const EMPTY_FILTERS = { q: "", status: "", type: "", source: "", storeId: "", application: "", from: "", to: "" };

export default function MailInbox() {
  const { lang, handleTokenExpiration } = useContext(Context);
  const t = MAIL_TEXT[lang === "ar" ? "ar" : "en"];
  const dir = lang === "ar" ? "rtl" : "ltr";

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [facets, setFacets] = useState({ types: [], sources: [], applications: [], stores: [] });
  const [openId, setOpenId] = useState(null);

  const expiredRef = useRef(handleTokenExpiration);
  useEffect(() => {
    expiredRef.current = handleTokenExpiration;
  });

  // typing waits a moment before it becomes a server query
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) => (current.q === search.trim() ? current : { ...current, q: search.trim() }));
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const params = useMemo(() => ({ ...filters, sort, order, page, limit }), [filters, sort, order, page, limit]);

  useEffect(() => {
    const token = localStorage.getItem("auth-token");
    if (!token) {
      expiredRef.current();
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    listMailsAPI(token, params, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setData(result);
      })
      .catch((failure) => {
        if (controller.signal.aborted || failure?.name === "AbortError") return;
        if (failure?.status === 401) expiredRef.current?.();
        setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [params, reload]);

  useEffect(() => {
    const token = localStorage.getItem("auth-token");
    if (!token) return undefined;
    const controller = new AbortController();
    getMailFacetsAPI(token, controller.signal)
      .then((result) => setFacets(result))
      .catch(() => null);
    return () => controller.abort();
  }, [reload]);

  const setFilter = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };
  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch("");
    setPage(1);
  };
  const hasFilters = Object.values(filters).some(Boolean);

  const all = { label: t.filters.all, value: "" };
  const statusOptions = [all, ...["sent", "failed", "pending", "uncertain", "skipped"].map((value) => ({ value, label: t.status[value] }))];
  const typeOptions = [all, ...facets.types.map((value) => ({ value, label: labelFor(TYPE_LABELS, lang, value) }))];
  const sourceOptions = [all, ...facets.sources.map((value) => ({ value, label: labelFor(SOURCE_LABELS, lang, value) }))];
  const storeOptions = [all, ...facets.stores.map((store) => ({ value: store.id, label: store.name }))];
  const appOptions = [all, ...facets.applications.map((value) => ({ value, label: value }))];

  const summary = data?.summary;
  const card = (key, status) => (
    <StatCard
      key={key}
      label={t.cards[key]}
      value={summary ? summary[key] : "—"}
      loading={loading && !summary}
      active={filters.status === status}
      onClick={() => {
        setFilters((current) => ({ ...current, status: current.status === status ? "" : status }));
        setPage(1);
      }}
    />
  );

  const columns = [
    {
      key: "subject",
      label: t.columns.subject,
      render: (mail) => (
        <div className="mail-cell-subject">
          <strong dir="auto">{mail.subject || "—"}</strong>
          <small dir="ltr">{mail.from || ""}</small>
          {mail.attachmentCount > 0 && (
            <span className="mail-clip">
              <Paperclip size={12} aria-hidden="true" /> {mail.attachmentCount}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "to",
      label: t.columns.to,
      mobile: false,
      render: (mail) => (
        <div className="mail-cell-recipients" dir="ltr">
          {mail.to.slice(0, 2).map((address) => (
            <span key={address}>{address}</span>
          ))}
          {mail.to.length > 2 && <small>{t.more(mail.to.length - 2)}</small>}
        </div>
      ),
    },
    {
      key: "type",
      label: t.columns.type,
      mobile: false,
      render: (mail) => (
        <div className="mail-cell-type">
          <Tags variant="neutral" size="sm">
            {labelFor(TYPE_LABELS, lang, mail.type)}
          </Tags>
          <small>{labelFor(SOURCE_LABELS, lang, mail.source)}</small>
        </div>
      ),
    },
    {
      key: "store",
      label: t.columns.store,
      mobile: false,
      render: (mail) => <span dir="auto">{mail.storeName || mail.storeId || "—"}</span>,
    },
    {
      key: "status",
      label: t.columns.status,
      render: (mail) => (
        <div className="mail-cell-subject">
          <EmailStatusTag status={mail.status} labels={{ status: { ...t.status, none: t.status.pending } }} />
          {mail.nextRetryAt && mail.status === "failed" && (
            <small>
              {t.detail.nextRetry}: {formatDateTime(mail.nextRetryAt, lang)}
            </small>
          )}
          {mail.attemptCount > 1 && (
            <small>
              {t.detail.attemptsUsed}: {mail.attemptCount}
              {mail.maxAttempts ? ` / ${mail.maxAttempts}` : ""}
            </small>
          )}
        </div>
      ),
    },
    { key: "date", label: t.columns.date, render: (mail) => <span className="mail-cell-date">{formatDateTime(mail.createdAt, lang)}</span> },
  ];

  const pagination = data?.pagination;
  const rows = data?.mails || [];
  const showTable = !error && (loading || rows.length > 0);

  const afterChange = useCallback(() => setReload((n) => n + 1), []);

  return (
    <div className="mail-page" dir={dir}>
      <SectionHeader
        icon={Mail}
        title={t.title}
        description={t.subtitle}
        actions={
          <Button variant="neutral" appearance="outline" icon={<RefreshCw size={15} />} loading={loading && Boolean(data)} onClick={() => setReload((n) => n + 1)}>
            {t.refresh}
          </Button>
        }
      />

      <section className="mail-cards" aria-label={t.title}>
        <StatCard label={t.cards.total} value={summary ? summary.total : "—"} loading={loading && !summary} active={!filters.status} onClick={() => setFilter("status")("")} />
        {card("sent", "sent")}
        {card("failed", "failed")}
        {card("pending", "pending")}
      </section>

      <section className="mail-filters" aria-label={t.filters.reset}>
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t.search} aria-label={t.search} iconStart={<Search size={16} />} className="mail-search" />
        <div className="mail-filter-grid">
          <Select label={t.filters.status} value={filters.status} onChange={setFilter("status")} options={statusOptions} />
          <Select label={t.filters.type} value={filters.type} onChange={setFilter("type")} options={typeOptions} />
          <Select label={t.filters.source} value={filters.source} onChange={setFilter("source")} options={sourceOptions} />
          <Select label={t.filters.store} value={filters.storeId} onChange={setFilter("storeId")} options={storeOptions} />
          <Select label={t.filters.app} value={filters.application} onChange={setFilter("application")} options={appOptions} />
          <Input type="date" label={t.filters.from} value={filters.from} max={filters.to || undefined} onChange={setFilter("from")} />
          <Input type="date" label={t.filters.to} value={filters.to} min={filters.from || undefined} onChange={setFilter("to")} />
          <Select
            label={t.filters.sort}
            value={sort}
            onChange={(event) => {
              setSort(event.target.value);
              setPage(1);
            }}
            options={["date", "status", "subject"].map((value) => ({ value, label: t.sorts[value] }))}
          />
          <Select
            label={t.filters.order}
            value={order}
            onChange={(event) => {
              setOrder(event.target.value);
              setPage(1);
            }}
            options={["desc", "asc"].map((value) => ({ value, label: t.orders[value] }))}
          />
          <Select
            label={t.filters.pageSize}
            value={String(limit)}
            onChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(1);
            }}
            options={PAGE_SIZES.map((size) => ({ value: String(size), label: String(size) }))}
          />
        </div>
        {hasFilters && (
          <Button variant="neutral" appearance="text" size="sm" onClick={resetFilters}>
            {t.filters.reset}
          </Button>
        )}
      </section>

      {error ? (
        <EmptyState variant="error" icon={<RefreshCw size={22} />} title={t.errorTitle} action={<Button variant="danger" appearance="outline" onClick={() => setReload((n) => n + 1)}>{t.retryLoad}</Button>}>
          {t.errorText}
        </EmptyState>
      ) : showTable ? (
        <>
          <Table className="mail-table" columns={columns} data={rows} loading={loading && rows.length === 0} emptyText={t.loading} clickable={(mail) => setOpenId(mail.id)} />
          {pagination && (
            <Pagination page={pagination.page} pages={pagination.pages} total={pagination.total} onPage={setPage} disabled={loading} labels={t.pagination} />
          )}
        </>
      ) : (
        <EmptyState icon={<Mail size={22} />} title={hasFilters ? t.noMatchTitle : t.emptyTitle} action={hasFilters ? <Button variant="neutral" appearance="outline" onClick={resetFilters}>{t.filters.reset}</Button> : null}>
          {hasFilters ? t.noMatchText : t.emptyText}
        </EmptyState>
      )}

      {openId && <MailDetails id={openId} lang={lang} t={t} onClose={() => setOpenId(null)} onChanged={afterChange} />}
    </div>
  );
}
